import { memo } from 'react';
import { TrendArrowDown } from 'assets/svg/TrendArrowDown';
import TrendArrowUp from 'assets/svg/TrendArrowUp';

type WhitespaceKpi = {
    kpi_id?: string;
    value?: string | number | boolean;
    growth_percentage?: number | null;
    valuePrefix?: string;
    valueSuffix?: string;
    display_label?: string;
};

type WhitespaceHierarchyItem = {
    attribute_type?: string;
    description?: string;
    ex_id?: string;
};

export type WhitespaceTooltipItem = {
    ex_id?: string;
    description?: string;
    is_own_attribute?: boolean;
    hierarchy?: WhitespaceHierarchyItem[];
    kpis?: WhitespaceKpi[];
};

const KPI_KEYS = {
    totalValue: ['activate_value_sales', 'value_sales'],
    avgPrice: ['avg_value_sales'],
};

const getKpiByIds = (kpis: WhitespaceKpi[] = [], ids: string[]) =>
    kpis.find((kpi) => ids.includes((kpi.kpi_id ?? '').toLowerCase()));

const toTitleCase = (value?: string) =>
    String(value ?? '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());

const WhitespaceItemTooltip = memo(
    ({ item, selectedYAxisKpiId }: { item: WhitespaceTooltipItem; selectedYAxisKpiId?: string }) => {
    const totalValueKpi = getKpiByIds(item.kpis, KPI_KEYS.totalValue);
    const avgPriceKpi = item.kpis?.find(item => item.kpi_id === selectedYAxisKpiId);
    const growthValue =
        typeof totalValueKpi?.growth_percentage === 'number' ? Math.abs(totalValueKpi.growth_percentage) : null;
    const growthDirection = totalValueKpi?.growth_percentage && totalValueKpi.growth_percentage < 0 ? 'down' : 'up';
    const hierarchyRows = item.hierarchy ?? [];

    return (
        <div className="w-[360px] rounded-xl bg-white p-5 shadow-[0px_3px_6px_-4px_rgba(0,0,0,0.12),0px_9px_28px_8px_rgba(0,0,0,0.05),0px_6px_16px_rgba(0,0,0,0.08)]">
            <p className="text-[16px] font-semibold leading-[22px] text-[#18181b]">{item.description ?? item.ex_id ?? '-'}</p>
            <div className="mt-5 flex items-start gap-10">
                <div>
                    <p className="text-xs text-[#71717b]">Total Value</p>
                    <p className="text-[14px] font-medium leading-[20px] text-[#18181b]">
                        {`${totalValueKpi?.valuePrefix ?? ''}${totalValueKpi?.value ?? '-'}${totalValueKpi?.valueSuffix ?? ''}`}
                    </p>
                </div>
                <div>
                    <p className="text-xs text-[#71717b]">{avgPriceKpi?.display_label}</p>
                    <p className="text-[14px] font-medium leading-[20px] text-[#18181b]">
                        {`${avgPriceKpi?.valuePrefix ?? ''}${avgPriceKpi?.value ?? '-'}`}
                    </p>
                </div>
                <div>
                    <p className="text-xs text-[#71717b]">Growth</p>
                    <p
                        className={`inline-flex items-center gap-1 text-[14px] font-medium leading-[20px] ${
                            growthDirection === 'down' ? 'text-[#d92d20]' : 'text-[#007a41]'
                        }`}
                    >
                        {growthValue != null ? (
                            <>
                                {growthDirection === 'down' ? (
                                    <TrendArrowDown className="shrink-0" color="#d92d20" />
                                ) : (
                                    <TrendArrowUp className="shrink-0" color="#007a41" />
                                )}
                                <span>{`${growthValue}%`}</span>
                            </>
                        ) : (
                            '-'
                        )}
                    </p>
                </div>
            </div>
            <div className="my-4 h-px bg-[#d4d4d8]" />
            <div className="flex flex-col gap-2">
                {hierarchyRows.map((row, index) => (
                    <div key={`${row.attribute_type ?? 'row'}-${index}`} className="flex items-start justify-between gap-6">
                        <p className="text-xs text-[#71717b]">{toTitleCase(row.attribute_type)}</p>
                        <p className="text-right text-[14px] font-medium leading-[16px] text-[#18181b]">
                            {row.description ?? row.ex_id ?? '-'}
                        </p>
                    </div>
                ))}
            </div>
        </div>
    );
    },
);

export default WhitespaceItemTooltip;
